import NiceModal from '@ebay/nice-modal-react';
import { t } from 'i18next';

import { revokeApiKey } from 'api/meedioBackend/apiKeys/revokeApiKey';
import { ReactComponent as Check } from 'assets/icons/Check.svg';
import { ReactComponent as Delete } from 'assets/icons/Delete.svg';
import ConfirmationModal from 'components/ConfirmationModal';
import { ToastOptions } from 'modules/ToastList/types';

import ApiKeyActionErrorModal from './ApiKeyActionErrorModal';

interface RevokeApiKeyConfirmArgs {
  /** Backend workspace UUID (already resolved by the caller). */
  workspaceUuid: string;
  /** The key to revoke. */
  keyId: string;
  /** Invoked after a successful revoke so the caller refreshes both lists (AC-06-04). */
  onRevoked?: () => void;
  /** Shows the success toast once the key is revoked. */
  pushToast: (options: ToastOptions) => void;
}

/**
 * Revoke flow (Figma 6392:68223). There is no bespoke revoke modal — the confirmation step
 * reuses the generic `components/ConfirmationModal` (destructive variant). On 204 it closes
 * the confirm dialog and shows a success toast; on failure it opens the shared action-error
 * modal whose Try again re-issues the DELETE without re-confirming (AC-06-02/03/05).
 */
export const showRevokeApiKeyConfirm = ({
  workspaceUuid,
  keyId,
  onRevoked,
  pushToast,
}: RevokeApiKeyConfirmArgs): void => {
  const runRevoke = async (): Promise<void> => {
    try {
      await revokeApiKey(workspaceUuid, keyId);
      onRevoked?.();
      pushToast({
        variant: 'success',
        icon: Check,
        title: t('workspaces_settings.api_keys.revoked.title'),
        description: t('workspaces_settings.api_keys.revoked.subtitle'),
      });
    } catch {
      void NiceModal.show(ApiKeyActionErrorModal, {
        operation: 'revoke',
        onRetry: () => {
          void runRevoke();
        },
      });
    }
  };

  void NiceModal.show(ConfirmationModal, {
    message: (
      <span className="flex flex-col gap-1">
        <span className="text-grayscale-black font-semibold">{t('workspaces_settings.api_keys.revoke.title')}</span>
        <span>{t('workspaces_settings.api_keys.revoke.message')}</span>
      </span>
    ),
    confirmButtonText: t('workspaces_settings.api_keys.revoke.confirm'),
    icon: Delete,
    confirmButtonVariant: 'destructive',
    onConfirm: runRevoke,
  });
};

export default showRevokeApiKeyConfirm;
